import { Component, inject, Input, output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IKorviuInputErrorMessage,
  KorviuInputComponent
} from '../../../../shared/components/korviu-input/korviu-input.component';
import { KorviuLoaderComponent } from '../../../../shared/components/korviu-loader/korviu-loader.component';
import { KorviuTextareaComponent } from '../../../../shared/components/korviu-textarea/korviu-textarea.component';

import { TranslateModule } from '@ngx-translate/core';
import { GOOD_PARTNER_MAX_LENGTH, IReferral } from 'core/types/referral.interface';
import { User } from '../../../../shared/types/user.model';
import { AuthService } from 'core/services/auth.service';
import { optionalWebsiteUrlValidator } from 'shared/utils/website-url.utils';

@Component({
  selector: 'app-ganasocio-user-form',
  standalone: true,
  imports: [
    FormsModule,
    KorviuInputComponent,
    KorviuLoaderComponent,
    KorviuTextareaComponent,
    TranslateModule,
    ReactiveFormsModule
  ],
  templateUrl: './ganasocio-user-form.component.html',
  styleUrl: './ganasocio-user-form.component.scss'
})
export class GanasocioUserFormComponent {
  private readonly authService = inject(AuthService);

  readonly onAddReferral = output<IReferral>();
  readonly goodPartnerMaxLength = GOOD_PARTNER_MAX_LENGTH;
  readonly referralForm = new FormGroup({
    id: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    first_name: new FormControl('', { nonNullable: true }),
    last_name: new FormControl('', { nonNullable: true }),
    phone_number: new FormControl('', { nonNullable: true }),
    country_code: new FormControl('', { nonNullable: true }),
    org_name: new FormControl('', { nonNullable: true }),
    email: new FormControl('', { nonNullable: true }),
    website_link: new FormControl('', { nonNullable: true }),
    good_partner: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(GOOD_PARTNER_MAX_LENGTH)]
    })
  });

  public readonly firstNameErrors: IKorviuInputErrorMessage[] = [
    { errorCode: 'required', localizedMessage: 'referral.validation.firstNameRequired' }
  ];
  public readonly lastNameErrors: IKorviuInputErrorMessage[] = [
    { errorCode: 'required', localizedMessage: 'referral.validation.lastNameRequired' }
  ];
  public readonly phoneNumberErrors: IKorviuInputErrorMessage[] = [
    { errorCode: 'required', localizedMessage: 'referral.validation.phoneNumberRequired' },
    { errorCode: 'pattern', localizedMessage: 'validation.invalidPhoneNumber' },
    { errorCode: 'minlength', localizedMessage: 'validation.invalidPhoneNumber' }
  ];

  public isLoading: boolean;

  @Input() set prefillData(value: IReferral) {
    this.referralForm.patchValue(value);
  }

  public get firstNameControl(): AbstractControl {
    return this.referralForm?.get('first_name');
  }

  public get lastNameControl(): AbstractControl {
    return this.referralForm?.get('last_name');
  }

  public get phoneNumberPrefixControl(): AbstractControl {
    return this.referralForm?.get('country_code');
  }

  public get phoneNumberControl(): AbstractControl {
    return this.referralForm?.get('phone_number');
  }

  public get orgNameControl(): AbstractControl {
    return this.referralForm?.get('org_name');
  }

  public get emailControl(): AbstractControl {
    return this.referralForm?.get('email');
  }

  public get websiteLinkControl(): AbstractControl {
    return this.referralForm?.get('website_link');
  }

  public get goodPartnerControl(): AbstractControl {
    return this.referralForm?.get('good_partner');
  }

  public get currentUser(): User {
    return this.authService.currentUser;
  }

  public get isValid(): boolean {
    return this.referralForm?.valid;
  }

  public onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    this.phoneNumberControl.setValue(input.value);
  }

  public validateAndSubmit(): void {
    this._setDefaultValidators();
    for (const control of Object.values(this.referralForm.controls)) {
      control.updateValueAndValidity();
      control.markAsDirty();
    }

    if (this.referralForm.valid) {
      this.onAddReferral.emit(this.referralForm.getRawValue());
    }
  }

  private _setDefaultValidators(): void {
    this.firstNameControl.setValidators([Validators.required]);
    this.lastNameControl.setValidators([Validators.required]);
    this.phoneNumberControl.setValidators([
      Validators.required,
      Validators.pattern('^[0-9]*$'),
      Validators.minLength(10)
    ]);
    this.phoneNumberPrefixControl.setValidators([Validators.required]);
    this.emailControl.setValidators([Validators.required, Validators.email]);
    this.goodPartnerControl.setValidators([Validators.required, Validators.maxLength(GOOD_PARTNER_MAX_LENGTH)]);
    this.websiteLinkControl.setValidators([optionalWebsiteUrlValidator]);
  }
}
